import React, { useEffect, useState } from "react";
import { Grid, Card, Typography, CardHeader, CardContent, Stack, Box, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, } from "@mui/material";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import errorHandler from "../../utils/errorHandler";
import CardLayout from "../../layouts/CardLayout";
import ApiService from "../../services/categoryManagementApi";
import { capitalize } from "../../utils/helpers";
import { formatOrderStatus } from "../../utils/CommonFunction";

const { orderManagementApi } = ApiService;

const OrderManagementView = () => {
  const { id } = useParams();
  const [data, setData] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await orderManagementApi.getById({ id: id });
        setData(response?.data?.data);
      } catch (error) {
        errorHandler(error);
      }
    };

    fetchData();
  }, [id]);

  return (
    <Box>
      <Helmet>
        <title>Order Management</title>
      </Helmet>
      <CardLayout>
        <CardHeader title="Order View" />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <Card sx={{ boxShadow: 4, borderRadius: 3, overflow: "hidden" }}>
                <CardContent>
                  <Stack spacing={2}>

                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      Customer Details
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell><strong>Name</strong></TableCell>
                            <TableCell>{data?.userId?.name || "N/A"}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Order ID</strong></TableCell>
                            <TableCell>{data?.orderNumber || "N/A"}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Phone</strong></TableCell>
                            <TableCell>
                              {data?.userId?.countryCode && data?.userId?.phoneNumber
                                ? `${data.userId.countryCode}${data.userId.phoneNumber}`
                                : "N/A"}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Delivery Address</strong></TableCell>
                            <TableCell>
                              {[
                                data?.userAddress?.houseNumber,
                                data?.userAddress?.streetNumber,
                                data?.userAddress?.district,
                                data?.userAddress?.commune,
                                data?.userAddress?.village,
                                data?.userAddress?.city,
                                data?.userAddress?.postalCode,
                              ]
                                .filter(Boolean)
                                .join(", ") || "N/A"}
                            </TableCell>
                          </TableRow>

                        </TableBody>
                      </Table>
                    </TableContainer>

                    {data?.dealerStatus === "accepted" && data?.dealerId && (
                      <>
                        <Divider />
                        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                          Dealer Details
                        </Typography>
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableBody>
                              <TableRow>
                                <TableCell><strong>Business Name</strong></TableCell>
                                <TableCell>{data.dealerId.businessName || "N/A"}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell><strong>Phone</strong></TableCell>
                                <TableCell>
                                  {data.dealerId.countryCode && data.dealerId.phoneNumber
                                    ? `${data.dealerId.countryCode}${data.dealerId.phoneNumber}`
                                    : "N/A"}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell><strong>Email</strong></TableCell>
                                <TableCell>{data.dealerId.email || "N/A"}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell><strong>City</strong></TableCell>
                                <TableCell>{data.dealerId.city || "N/A"}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell><strong>Village</strong></TableCell>
                                <TableCell>{data.dealerId.village || "N/A"}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}


                    {data?.driverDetails && (
                      <>
                        <Divider />
                        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                          Driver Details
                        </Typography>
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableBody>
                              <TableRow>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell>{data.driverDetails.name || "N/A"}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell><strong>Phone</strong></TableCell>
                                <TableCell>{data.driverDetails.phoneNumber || "N/A"}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell>{data.driverDetails.driverStatus || "N/A"}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}

                    <Divider />
                    <Typography variant="h6">Order Items</Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Product Name</TableCell>
                            <TableCell align="center">Quantity</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data?.products?.map((product, index) => (
                            <TableRow key={index}>
                              <TableCell>{product.name}</TableCell>
                              <TableCell align="center">{product.qty}</TableCell>
                              <TableCell align="right">${product.price}</TableCell>
                              <TableCell align="right">${product.qty * product.price}</TableCell>
                            </TableRow>
                          )) || (
                              <TableRow>
                                <TableCell colSpan={4} align="center">
                                  No Items Found
                                </TableCell>
                              </TableRow>
                            )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Divider />

                    <Typography variant="h6">Order Date/Time</Typography>
                    <Typography variant="body1" sx={{ overflowWrap: "break-word" }}>
                      {data?.scheduleDate ? new Date(data.scheduleDate).toLocaleString() : "N/A"}
                    </Typography>
                    <Divider />


                    <Typography variant="h6">Order Status</Typography>
                    <Typography variant="body1" sx={{ overflowWrap: "break-word" }}>
                      {formatOrderStatus(data?.orderStatus?.stage) || "pending"}
                    </Typography>
                    <Divider />



                    <Typography variant="h6">Payment Status</Typography>
                    <Typography variant="body1" sx={{ overflowWrap: "break-word" }}>
                      {data?.paymentStatus || "N/A"}
                    </Typography>
                    <Divider />

                    <Typography variant="h6">Total Amount</Typography>
                    <Typography variant="body1" sx={{ overflowWrap: "break-word" }}>
                      ${data?.total || "N/A"}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </CardLayout>
    </Box>
  );
};

export default OrderManagementView;
